/**
 * Hook for managing notification preferences.
 */

import { useState, useEffect, useCallback } from 'react';
import { useApiBase } from './useApiBase';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../lib/authFetch';

export type NotificationCategory = {
  key: string;
  label: string;
  description: string;
};

export type NotificationPreferences = {
  push_enabled: boolean;
  categories: Record<string, boolean>;
};

export type NotificationPreferencesState = {
  /** Current preferences */
  preferences: NotificationPreferences | null;
  /** Available categories with metadata */
  categories: NotificationCategory[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Update a single category preference */
  updateCategory: (key: string, enabled: boolean) => Promise<boolean>;
  /** Update the global push_enabled toggle */
  updatePushEnabled: (enabled: boolean) => Promise<boolean>;
  /** Refresh preferences from server */
  refresh: () => Promise<void>;
};

export function useNotificationPreferences(): NotificationPreferencesState {
  const apiBase = useApiBase();
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [categories, setCategories] = useState<NotificationCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch preferences
  const refresh = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(`${apiBase}/api/push-subscriptions/preferences`);

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();
      setPreferences(data.preferences);
      setCategories(data.categories || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setIsLoading(false);
    }
  }, [apiBase, user]);

  // Update category preference
  const updateCategory = useCallback(async (key: string, enabled: boolean): Promise<boolean> => {
    if (!user) return false;

    const previousPreferences = preferences;

    // Optimistic update
    setPreferences((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        categories: { ...prev.categories, [key]: enabled },
      };
    });

    try {
      const response = await authFetch(`${apiBase}/api/push-subscriptions/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categories: { [key]: enabled } }),
      });

      if (!response.ok) {
        throw new Error('Failed to update preference');
      }

      const data = await response.json();
      setPreferences(data.preferences);
      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update preference';
      setError(message);
      setPreferences(previousPreferences);
      return false;
    }
  }, [apiBase, user, preferences]);

  // Update global push enabled
  const updatePushEnabled = useCallback(async (enabled: boolean): Promise<boolean> => {
    if (!user) return false;

    const previousPreferences = preferences;

    // Optimistic update
    setPreferences((prev) => {
      if (!prev) return prev;
      return { ...prev, push_enabled: enabled };
    });

    try {
      const response = await authFetch(`${apiBase}/api/push-subscriptions/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ push_enabled: enabled }),
      });

      if (!response.ok) {
        throw new Error('Failed to update preference');
      }

      const data = await response.json();
      setPreferences(data.preferences);
      setError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update preference';
      setError(message);
      setPreferences(previousPreferences);
      return false;
    }
  }, [apiBase, user, preferences]);

  // Load preferences on mount
  useEffect(() => {
    if (user) {
      refresh();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    preferences,
    categories,
    isLoading,
    error,
    updateCategory,
    updatePushEnabled,
    refresh,
  };
}

export default useNotificationPreferences;
